import { PAYMENT_METHOD } from 'components/common/constants';
import { isBeneficiaryStateProvider } from 'services/utils';

// TRN-7602 — Convera API / Convera Merchant: the State/Province and / or the Routing Code of the beneficiary become
// mandatory for the countries below. Source: Convera Payment Formatting Guidelines 2026.1 (March 2026).
// The rules depend on the beneficiary BANK country only (the payment currency is not taken into account) and apply to the
// SWIFT payments — the only method whose form has the bank country and the Routing Code.

export const ROUTING_CODE_MIN_LENGTH = 4;
export const ROUTING_CODE_MAX_LENGTH = 11;

// `routingCode`: true — always mandatory, 'nonIban' — only when the account number is not an IBAN (UK).
export const CONVERA_COUNTRY_REQUIREMENTS = {
  AU: { routingCode: true, state: true },
  CA: { routingCode: true, state: true },
  US: { routingCode: true, state: true },
  MX: { state: true },
  NZ: { routingCode: true },
  GB: { routingCode: 'nonIban' },
  AZ: { routingCode: true },
  BR: { routingCode: true },
  BW: { routingCode: true },
  GH: { routingCode: true },
  GY: { routingCode: true },
  HK: { routingCode: true },
  ID: { routingCode: true },
  IN: { routingCode: true },
  JM: { routingCode: true },
  KE: { routingCode: true },
  LK: { routingCode: true },
  SG: { routingCode: true },
  ZA: { routingCode: true },
  VN: { routingCode: true },
  ZM: { routingCode: true }
};

// { isRoutingCodeRequired, isStateRequired } of a SWIFT payment of Convera API / Merchant to `bankCountry`.
// `hasIban` — the beneficiary account is given as an IBAN (the UK rule: a Routing Code only without it).
export const getConveraRequirements = ({ provider, paymentMethod, bankCountry, hasIban = false }) => {
  const rule = CONVERA_COUNTRY_REQUIREMENTS[bankCountry];

  if (!rule || !isBeneficiaryStateProvider(provider) || paymentMethod !== PAYMENT_METHOD.SWIFT) {
    return { isRoutingCodeRequired: false, isStateRequired: false };
  }

  return {
    isRoutingCodeRequired: rule.routingCode === 'nonIban' ? !hasIban : Boolean(rule.routingCode),
    isStateRequired: Boolean(rule.state)
  };
};

// Spaces and hyphens are stripped before the validation (and before sending: "12-34-56" → "123456").
export const normalizeRoutingCode = (value) => (value || '').replace(/[\s-]/g, '');

const LETTERS_AND_DIGITS = /^[A-Za-z0-9]+$/;

// The one rule for every listed country: non-empty, letters and digits only, 4 to 11 characters.
// Returns 'empty' | 'format' | null (the length is part of 'format': one message says it all).
export const getRoutingCodeError = (value) => {
  const code = normalizeRoutingCode(value);

  if (!code) return 'empty';
  if (
    !LETTERS_AND_DIGITS.test(code) ||
    code.length < ROUTING_CODE_MIN_LENGTH ||
    code.length > ROUTING_CODE_MAX_LENGTH
  ) {
    return 'format';
  }

  return null;
};
